import clsx from "clsx";
import { FC, ReactNode } from "react";

import type { SubtaskEvent } from "@tsmono/inspect-common/types";
import { MetaDataGrid } from "@tsmono/inspect-components/content";
import { Icon } from "@tsmono/react/icons";
import { isRecord } from "@tsmono/util";

import { EventPanel } from "./event/EventPanel";
import { formatTiming, formatTitle } from "./event/utils";
import styles from "./SubtaskEventView.module.css";
import { EventNode, EventPanelCallbacks, EventType } from "./types";

interface SubtaskEventViewProps {
  eventNode: EventNode<SubtaskEvent>;
  childNodes: EventNode<EventType>[];
  className?: string;
  eventCallbacks?: EventPanelCallbacks;
}

export const SubtaskEventView: FC<SubtaskEventViewProps> = ({
  eventNode,
  childNodes,
  className,
  eventCallbacks,
}) => {
  const event = eventNode.event;
  const body: ReactNode[] = [];
  if (event.type === "fork") {
    body.push(
      <div title="Summary" className={clsx(styles.summary)}>
        <div className={clsx("text-style-label")}>Inputs</div>
        <div className={clsx(styles.summaryRendered)}>
          <Rendered values={event.input} />
        </div>
      </div>
    );
  } else {
    body.push(
      <SubtaskSummary
        data-name="Summary"
        input={event.input}
        result={event.result}
      />
    );
  }

  const type = event.type === "fork" ? "Fork" : "Subtask";
  return (
    <EventPanel
      eventNodeId={eventNode.id}
      className={className}
      title={formatTitle(
        `${type}: ${event.name}`,
        undefined,
        event.working_time
      )}
      subTitle={
        event.timestamp
          ? formatTiming(event.timestamp, event.working_start)
          : undefined
      }
      childIds={childNodes.map((child) => child.id)}
      collapseControl="bottom"
      eventCallbacks={eventCallbacks}
    >
      {body}
    </EventPanel>
  );
};

interface SubtaskSummaryProps {
  input: Record<string, unknown>;
  result: unknown;
}

const SubtaskSummary: FC<SubtaskSummaryProps> = ({ input, result }) => {
  const output = typeof result === "object" ? result : { result };
  return (
    <div className={clsx(styles.subtaskSummary)}>
      <div className={clsx("text-style-label", "text-size-small")}>Input</div>
      <div className={clsx("text-size-large", styles.subtaskLabel)}></div>
      <div className={clsx("text-style-label", "text-size-small")}>Output</div>
      <Rendered values={input} />
      <div className={clsx("text-size-title-secondary", styles.subtaskLabel)}>
        <Icon name="arrowRight" />
      </div>
      <div>{output ? <Rendered values={output} /> : "-"}</div>
    </div>
  );
};

interface RenderedProps {
  values: unknown;
}

const Rendered: FC<RenderedProps> = ({ values }): ReactNode => {
  if (Array.isArray(values)) {
    return values.map((val: unknown, index) => (
      <Rendered key={index} values={val} />
    ));
  }
  if (isRecord(values)) {
    return Object.keys(values).length === 0 ? (
      <None />
    ) : (
      <MetaDataGrid entries={values} />
    );
  }
  if (typeof values === "string") {
    return values;
  }
  if (typeof values === "number" || typeof values === "boolean") {
    return String(values);
  }
  // Null, or an object shape a subtask's input/result doesn't carry. String()
  // would print "[object Object]" for the latter.
  return <None />;
};

const None: FC = () => {
  return (
    <span className={clsx("text-size-small", "text-style-secondary")}>
      [None]
    </span>
  );
};
